// App-injected admin runtime. The API client is created per-vertical (its auth
// token store is vertical-specific), so shared admin components/pages can't
// import a concrete `api`. Each app registers its bundle at module load (same
// pattern as i18n registerDictionaries) and shared code reads it via getApi()/
// adminRuntime() inside handlers/effects — after registration has run.
import type { AdminApi } from "./api";
import type { Staff } from "./types";

export type AdminUserPayload = {
  sub: string;
  email?: string;
  name?: string;
  role?: string;
  /** From /me, so it is available to every role — unlike /settings, which is admin-only. */
  companyId?: number;
  timezone?: string;
};

/**
 * A vertical-specific multi-select rendered in the staff edit modal.
 *
 * What it selects over differs per vertical, which is the reason it is injected
 * rather than written into the shared page: gym picks membership plans (プラン),
 * clinic and fudousan pick services (サービス). Both narrow which staff the
 * availability engine will offer, one through staff_plans and one through
 * staff_services. The
 * staff page is ONE file re-exported one-line by all three verticals, and it
 * reaches the server only through getApi(): AdminApi — which has no `plans` and
 * must not grow one, because clinic and fudousan have no membership layer to
 * describe. So the capability is injected the way the api itself is, and a
 * vertical that leaves it undefined renders no section and issues no request.
 *
 * A data seam rather than a render prop: the SAVE has to be sequenced by the page
 * — after the PATCH on edit, after the POST returns an id on create — and only
 * the page knows when that is.
 *
 * Labels come from the ordinary dictionary under fixed keys (staffLinks,
 * staffLinksHint, staffLinksNone), so only the vertical that registers this needs
 * to define them.
 */
export interface StaffLinkSection {
  /** Every option, fetched once when the page loads. */
  loadOptions: () => Promise<{ id: number; name: string }[]>;
  /*
    The current selection.

    Takes the whole row, not an id: clinic and fudousan select over services, and
    the staff payload already carries them, so their implementation reads the row
    and issues no request at all. Gym's plans are a separate endpoint and ignore
    the row beyond its id.
  */
  loadSelection: (staff: Staff) => Promise<number[]>;
  /** Replace the selection. An empty array means none — not "unrestricted". */
  save: (staffId: number, ids: number[]) => Promise<void>;
  /**
   * The dictionary key the section's wording hangs off: the label itself, then
   * `<key>Hint`, `<key>None` and `<key>SaveFailed`. Default "staffLinks". Needed
   * once an app registers two sections -- one admin serving a clinic and a gym
   * (teisyovision plan.md phase 13) links staff to services AND plans.
   */
  labelKey?: string;
}

export interface AdminRuntime {
  api: AdminApi;
  getValidAccessToken: () => Promise<string | null>;
  ensureSessionOrRedirect: (
    redirect: (path: string) => void,
  ) => Promise<AdminUserPayload | null>;
  getCurrentUser: () => AdminUserPayload | null;
  setOnSessionExpired: (cb: () => void) => void;
  clearTokens: () => void;
  /** The stored id_token, used as the id_token_hint on RP-initiated logout. */
  getIdToken: () => string | null;
  /** Optional: see StaffLinkSection. One section, several, or none. */
  staffLinks?: StaffLinkSection | StaffLinkSection[];
  /**
   * Optional: the company serves two product lines from one database, a
   * clinic and a gym (teisyovision plan.md phase 13). The services page then
   * shows and edits each service's kind and filters by it, and the settings
   * page offers the opening special's scope. `plans` lists the gym plans a
   * scope can name.
   */
  productLines?: { plans: () => Promise<{ id: number; name: string }[]> };
}

let runtime: AdminRuntime | null = null;

export function registerAdminRuntime(rt: AdminRuntime) {
  runtime = rt;
}

export function adminRuntime(): AdminRuntime {
  if (!runtime) {
    throw new Error(
      "Admin runtime not registered — ensure the app's lib/api module is imported before render.",
    );
  }
  return runtime;
}

export function getApi(): AdminApi {
  return adminRuntime().api;
}

/**
 * The staff-link sections, [] where the vertical registers none.
 *
 * Reads `runtime` directly rather than through adminRuntime(): this is called
 * during render, where the throwing accessor would be wrong for something that is
 * legitimately absent.
 */
export function staffLinkSections(): StaffLinkSection[] {
  const s = runtime?.staffLinks;
  if (!s) return [];
  return Array.isArray(s) ? s : [s];
}

/** The product-line seam, or undefined in a single-line app. Same read as above. */
export function productLines(): AdminRuntime["productLines"] {
  return runtime?.productLines;
}
